import { PERMISSIONS } from '@ibe/constants';
import { Button, cn, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../auth/auth-context.js';
import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { CancellationPolicyListPage } from '../../cancellation-policies/index.js';
import { PaymentPolicyListPage } from '../../payment-policies/index.js';
import { PropertyGroupListPage } from '../../property-groups/index.js';
import { PropertyImagesPage } from '../../property-images/index.js';
import { PropertySetupPage } from '../../property-setup/index.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { propertyEndpoints } from '../api/endpoints.js';
import { useSoftDeleteProperty } from '../api/mutations.js';
import { PROPERTY_STATUS_OPTIONS, type PropertyRow, propertyRow } from '../api/schemas.js';

/**
 * The properties list.
 *
 * Search, sort, pagination, filtering and the three non-data states all come
 * from `ResourceListPage`. What is here is the descriptor: which columns
 * exist, which backend fields each one reads, and what a row can be done to.
 *
 * The columns are a descriptor because that is what makes them selectable.
 * Each declares the fields it reads, they become `?fields=`, and the service
 * projects exactly those — so hiding a column genuinely stops fetching it.
 * Every id below that is marked `sortable` is also a value the service will
 * sort on; a column sorted by a name the service does not know answers 422.
 */

const filters: readonly FilterDescriptor[] = [
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    // The full vocabulary, not the two the list used to offer. A DRAFT
    // property that no filter could select was one an operator could only
    // find by paging.
    options: PROPERTY_STATUS_OPTIONS,
  },
];

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const propertyColumns: readonly ColumnDescriptor<PropertyRow>[] = [
  {
    id: 'name',
    header: 'Property',
    sortable: true,
    // Carries the row link, so it cannot be hidden.
    alwaysVisible: true,
    // Two fields, one column: an operator knows a property by name and code,
    // and a separate code column costs width the filters need.
    fields: ['name', 'code'],
    // Every field is optional on the row: the reader's column choice decides
    // what the service projects. Falling back to the id beats rendering a link
    // with no text if a field is ever missing.
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/properties/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'displayName',
    header: 'Display name',
    sortable: true,
    fields: ['displayName'],
    // Off by default: it is usually the name again, and a column that repeats
    // its neighbour costs width for nothing.
    defaultVisible: false,
    cell: (row) => row.displayName,
  },
  {
    id: 'city',
    header: 'Location',
    fields: ['city', 'countryCode'],
    cell: (row) =>
      [row.city, row.countryCode].filter((part) => part !== null && part !== undefined).join(', '),
  },
  {
    id: 'propertyType',
    header: 'Type',
    fields: ['propertyType'],
    defaultVisible: false,
    cell: (row) => row.propertyType,
  },
  {
    id: 'starRating',
    header: 'Rating',
    sortable: true,
    fields: ['starRating'],
    defaultVisible: false,
    // The star is decoration beside the number, so it is hidden from the
    // accessible name rather than announced as "black star".
    cell: (row) =>
      row.starRating === null || row.starRating === undefined ? null : (
        <span>
          {row.starRating}
          <span aria-hidden="true"> ★</span>
        </span>
      ),
  },
  {
    id: 'currency',
    header: 'Currency',
    fields: ['currency'],
    defaultVisible: false,
    cell: (row) => row.currency,
  },
  {
    id: 'timezone',
    header: 'Time zone',
    fields: ['timezone'],
    defaultVisible: false,
    cell: (row) => row.timezone,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    // An icon-only header reads as nothing in a screen reader's table
    // navigation, so it gets an explicit name.
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    // Declares `name` although it renders no visible text: the action labels
    // name the row for a screen reader, so the column reads the field even
    // though it does not show it. With `fields: []` and projection real,
    // hiding the Property column made every action announce "View undefined".
    fields: ['name'],
    cell: (row: PropertyRow): React.JSX.Element => <PropertyRowActions row={row} />,
  },
];

/**
 * The per-row controls, as a component rather than inline markup.
 *
 * Deleting needs a mutation, a confirmation and a permission check, and a
 * column descriptor's `cell` is a plain function — it cannot hold hooks. This
 * is the smallest thing that can.
 */
function PropertyRowActions({ row }: { readonly row: PropertyRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteProperty();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/properties/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/properties/${row.id}/edit` },
          ]}
        />
        {/* Offered only to someone who may actually set up a property. */}
        <PermissionGuard permission={PERMISSIONS.PROPERTY_SETUP}>
          <RowActions
            actions={[
              {
                label: `Set up ${label}`,
                icon: Wrench,
                to: `/property-setup?propertyId=${encodeURIComponent(row.id)}`,
              },
            ]}
          />
        </PermissionGuard>
        {/* Offered only to someone who may actually delete. A control that
            always answers 403 teaches people to distrust every other button. */}
        <PermissionGuard permission={PERMISSIONS.PROPERTY_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete property"
        // Says what actually happens. "Delete" alone reads as irreversible,
        // and an operator who believes that will not ask for it back.
        description={`${label} will be removed from this list and can no longer be sold. Its reservations, payments and history are retained, not erased.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

function PropertiesListView(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Properties"
      description="Every property this account manages."
      path={propertyEndpoints.list}
      resource="properties"
      schema={propertyRow}
      columns={propertyColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PROPERTY_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PROPERTY_CREATE}>
          {/* A link, not a button with a handler: creating is a page, so it
              should be openable in a new tab and reachable by URL. */}
          <Button asChild>
            <Link to="/properties/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New property
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}

const PROPERTY_TABS = [
  {
    id: 'properties',
    label: 'Properties',
    permission: PERMISSIONS.PROPERTY_READ,
    component: PropertiesListView,
  },
  {
    id: 'property-setup',
    label: 'Property setup',
    permission: PERMISSIONS.PROPERTY_SETUP,
    component: PropertySetupPage,
  },
  {
    id: 'property-images',
    label: 'Property images',
    permission: PERMISSIONS.PROPERTY_READ,
    component: PropertyImagesPage,
  },
  {
    id: 'payment-policies',
    label: 'Payment policy',
    permission: PERMISSIONS.PAYMENT_POLICY_READ,
    component: PaymentPolicyListPage,
  },
  {
    id: 'cancellation-policies',
    label: 'Cancellation policy',
    permission: PERMISSIONS.CANCELLATION_POLICY_READ,
    component: CancellationPolicyListPage,
  },
  {
    id: 'property-groups',
    label: 'Property group',
    permission: PERMISSIONS.PROPERTY_GROUP_READ,
    component: PropertyGroupListPage,
  },
] as const;

export function PropertyListPage(): React.JSX.Element {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState('properties');

  const visibleTabs = PROPERTY_TABS.filter((tab) => can(tab.permission));
  const ActiveComponent =
    visibleTabs.find((tab) => tab.id === activeTab)?.component ?? PropertiesListView;

  return (
    <section className="space-y-4">
      <nav aria-label="Property setup" className="border-b">
        <ul className="flex gap-1">
          {visibleTabs.map((tab) => {
            const active = tab.id === activeTab;
            return (
              <li key={tab.id}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors hover:text-foreground',
                    active
                      ? 'border-b-2 border-foreground text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {tab.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <ActiveComponent />
    </section>
  );
}
